import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Lightbulb, Brain, Target, TrendingUp, Sparkles, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Mind-Body Activities (internal correlations)
const mindBodyFactors = [
  { id: 'food_quality', label: 'Whole Foods vs Processed', field: 'processed_food_level' },
  { id: 'caffeine', label: 'Caffeine Intake', field: 'custom_medications' },
  { id: 'alcohol', label: 'Alcohol Consumption', field: 'alcohol_craving_resisted' },
  { id: 'sleep_duration', label: 'Sleep Duration', field: 'hours_slept' },
  { id: 'exercise', label: 'Physical Activity', field: 'exercise_duration' },
  { id: 'sunlight', label: 'Sunlight Exposure', field: 'sunlight_minutes' },
  { id: 'water', label: 'Hydration', field: 'water_intake' },
  { id: 'meal_timing', label: 'Late Night Eating', field: 'evening_reflection' }
];

// Outcome Metrics
const outcomeMetrics = [
  { id: 'mood', label: 'Mood & Emotional State', field: 'emotion' },
  { id: 'energy', label: 'Energy Level', field: 'energy_field' },
  { id: 'sleep_quality', label: 'Sleep Quality', field: 'sleep_quality' },
  { id: 'stress', label: 'Stress & Anxiety', field: 'mind_state' },
  { id: 'focus', label: 'Mental Clarity & Focus', field: 'mind_state' },
  { id: 'irritability', label: 'Irritability', field: 'emotion' }
];

// Internal Correlation Database (Therappi-only data)
const internalCorrelations: Record<string, Record<string, any>> = {
  'food_quality-mood': {
    improvement: 17,
    insight: 'Your mood improves significantly on days you eat whole foods. Nutrition is the foundation of emotional stability.',
    action: 'Try meal prepping whole foods for the week ahead.'
  },
  'food_quality-energy': {
    improvement: 20,
    insight: 'Processed foods create energy crashes. Whole foods provide sustained vitality throughout the day.',
    action: 'Replace one processed meal daily with whole foods.'
  },
  'caffeine-sleep_quality': {
    improvement: -25,
    insight: 'Caffeine after 3pm reduces your sleep quality by 25%. Your nervous system needs time to wind down.',
    action: 'Set a 3pm caffeine cutoff for better rest.'
  },
  'caffeine-mood': {
    improvement: -15,
    insight: 'High caffeine days show increased irritability. Stimulants elevate sympathetic arousal.',
    action: 'Replace afternoon caffeine with breathwork or water.'
  },
  'alcohol-sleep_quality': {
    improvement: -30,
    insight: 'Alcohol disrupts REM sleep and recovery. Even one drink impacts your nervous system restoration.',
    action: 'Try an alcohol-free evening ritual this week.'
  },
  'alcohol-energy': {
    improvement: -30,
    insight: 'Next-day energy drops 30% after alcohol. Your body prioritises detox over vitality.',
    action: 'Notice the pattern and consider alcohol-free evenings.'
  },
  'sleep_duration-mood': {
    improvement: 22,
    insight: 'Each extra hour of sleep improves mood by 22%. Sleep is emotional regulation.',
    action: 'Prioritise 8 hours of sleep tonight.'
  },
  'sleep_duration-energy': {
    improvement: 25,
    insight: 'Sleep duration directly predicts your energy. Your body needs 7-9 hours for full restoration.',
    action: 'Move bedtime 30 minutes earlier this week.'
  },
  'exercise-mood': {
    improvement: 18,
    insight: 'Movement releases endorphins and regulates cortisol. Exercise is mood medicine.',
    action: 'Add 20 minutes of movement to your day.'
  },
  'exercise-stress': {
    improvement: 16,
    insight: 'Physical activity reduces perceived stress through nervous system regulation.',
    action: 'Try a walk or gentle yoga when stressed.'
  },
  'sunlight-mood': {
    improvement: 15,
    insight: 'Morning sunlight boosts serotonin production. Light is your first antidepressant.',
    action: 'Get 15 minutes of morning sunlight daily.'
  },
  'sunlight-sleep_quality': {
    improvement: 12,
    insight: 'Morning light sets your circadian rhythm, improving evening sleep quality.',
    action: 'Start your day outside, before screens.'
  },
  'water-energy': {
    improvement: 10,
    insight: 'Even mild dehydration reduces energy by 25%. Water is cellular fuel.',
    action: 'Drink a glass of water upon waking.'
  },
  'meal_timing-sleep_quality': {
    improvement: -18,
    insight: 'Eating within 2 hours of sleep disrupts deep sleep stages. Your digestion needs time.',
    action: 'Finish eating 3 hours before bedtime.'
  }
};

export default function MindBodyInsights() {
  const [selectedFactor, setSelectedFactor] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [correlationKey, setCorrelationKey] = useState('');
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (selectedFactor && selectedOutcome) {
      setCorrelationKey(`${selectedFactor}-${selectedOutcome}`);
      loadUserData();
    }
  }, [selectedFactor, selectedOutcome]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;
      setUserData(data);
    } catch (error) {
      console.error('Error loading mind-body data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load correlation data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const correlation = internalCorrelations[correlationKey];
  const getImprovementColor = (value: number) => {
    if (value > 15) return 'text-green-500';
    if (value > 0) return 'text-emerald-500';
    if (value < 0) return 'text-amber-500';
    return 'text-muted-foreground';
  };

  const getImprovementBadge = (value: number) => {
    const isPositive = value > 0;
    return (
      <Badge 
        variant={isPositive ? "default" : "destructive"}
        className="text-lg font-bold gap-1"
      >
        {isPositive ? '+' : ''}{value}%
        {isPositive ? <TrendingUp className="w-4 h-4" /> : null}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="w-6 h-6 text-primary" />
            Mind-Body Insights
          </CardTitle>
          <CardDescription className="text-base">
            Discover how your lifestyle choices shape your wellbeing. These insights analyse your daily check-in data to reveal powerful correlations between your habits and how you feel.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Selector Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Lifestyle Factor</CardTitle>
            <CardDescription>
              Select a habit or behaviour to analyse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedFactor} onValueChange={setSelectedFactor}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a factor..." />
              </SelectTrigger>
              <SelectContent>
                {mindBodyFactors.map(factor => (
                  <SelectItem key={factor.id} value={factor.id}>
                    {factor.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Wellbeing Outcome</CardTitle>
            <CardDescription>
              Select what you want to understand
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedOutcome} onValueChange={setSelectedOutcome}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose an outcome..." />
              </SelectTrigger>
              <SelectContent>
                {outcomeMetrics.map(metric => (
                  <SelectItem key={metric.id} value={metric.id}>
                    {metric.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Insight Display */}
      {correlation && selectedFactor && selectedOutcome && (
        <Card className="bg-gradient-to-br from-background via-primary/5 to-accent/5 border-2 border-primary/20">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Heart className="w-5 h-5 text-primary" />
                  {mindBodyFactors.find(f => f.id === selectedFactor)?.label.split(' ').slice(1).join(' ')}
                  <span className="text-muted-foreground">→</span>
                  <Brain className="w-5 h-5 text-accent" />
                  {outcomeMetrics.find(m => m.id === selectedOutcome)?.label.split(' ').slice(1).join(' ')}
                </CardTitle>
                <Badge variant="outline">Mind-Body Intelligence</Badge>
              </div>
              {getImprovementBadge(correlation.improvement)}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Impact Visualisation */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Impact on Wellbeing</span>
                <span className={`font-bold ${getImprovementColor(correlation.improvement)}`}>
                  {correlation.improvement > 0 ? '+' : ''}{correlation.improvement}%
                </span>
              </div>
              <Progress 
                value={Math.abs(correlation.improvement)} 
                className="h-3"
              />
            </div>

            {/* Your Pattern */}
            <div className="rounded-xl bg-primary/5 p-5 space-y-2 border border-primary/10">
              <div className="flex items-center gap-2 text-primary font-semibold">
                <TrendingUp className="w-5 h-5" />
                <span>Your Pattern:</span>
              </div>
              <p className="text-foreground">
                {outcomeMetrics.find(m => m.id === selectedOutcome)?.label} 
                {correlation.improvement > 0 ? ' improves' : ' changes'} by {Math.abs(correlation.improvement)}% 
                based on your {mindBodyFactors.find(f => f.id === selectedFactor)?.label.toLowerCase()}.
              </p>
            </div>

            {/* Therappi Insight */}
            <div className="rounded-xl bg-primary/10 p-5 space-y-2 border-2 border-primary/20">
              <div className="flex items-center gap-2 text-primary font-semibold">
                <Sparkles className="w-5 h-5" />
                <span>Therappi<sup className="text-[0.6em]">®</sup> Insight:</span>
              </div>
              <p className="text-foreground font-medium leading-relaxed">
                {correlation.insight}
              </p>
            </div>

            {/* Action Step */}
            <div className="rounded-xl bg-gradient-to-r from-accent/10 to-primary/10 p-5 space-y-2 border border-primary/20">
              <div className="flex items-center gap-2 text-accent font-semibold">
                <Target className="w-5 h-5" />
                <span>Action Step:</span>
              </div>
              <p className="text-foreground font-medium">
                {correlation.action}
              </p>
            </div>

            {/* Connect to WHOOP CTA */}
            <div className="rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 p-5 border border-primary/20">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Unlock Deeper Insights</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Connect WHOOP® to see how these patterns correlate with your HRV, recovery, and sleep stages for even more personalised guidance.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!correlation && (selectedFactor || selectedOutcome) && (
        <Card className="p-12 text-center">
          <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">
            Select both a factor and an outcome to reveal your insights
          </p>
        </Card>
      )}

      {/* Initial State */}
      {!selectedFactor && !selectedOutcome && (
        <Card className="bg-gradient-to-br from-primary/5 to-accent/5 p-12 text-center border-2 border-dashed border-primary/20">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Heart className="w-8 h-8 text-primary" />
              <Sparkles className="w-8 h-8 text-accent" />
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold">
              Your Habits Shape Your Reality
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Every choice creates a ripple. Discover how your daily patterns influence your wellbeing through data-driven self-awareness.
            </p>
            <p className="text-base text-muted-foreground">
              Select a factor and outcome above to explore your personal correlations.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
